import {
  LATEST_FINDINGS,
  CATEGORIES,
  SEVERITY_ORDER,
  SEVERITY_LABELS,
  findingsToCsv,
  computeScore,
  type Finding,
  type FindingStatus,
  type Check,
} from "./audit-data";
import { buildFindingsSummary, type FindingsSummaryDto } from "./ai-api";
import { createFilterableTable, type FtColumn, type FilterDef } from "../ui/filterable-table";
import { downloadCsv } from "../ui/csv";
import { createSeverityBadge } from "./components/severity-badge";
import { createDrawer } from "../ui/drawer";
import { createFindingDetail } from "./finding-detail";
import type { AssistantContext } from "./sections";

export interface FindingsDeps {
  readonly openAssistant: (context: AssistantContext, summary?: FindingsSummaryDto) => void;
  // 実接続時にインジェクトされる診断結果。省略時はモックサンプルにフォールバック。
  readonly findings?: readonly Finding[];
  // 本診断の対象外チェック（正直な "未評価" 表示用）。省略時は非表示。
  readonly notEvaluated?: readonly Check[];
  // 接続を試みたが未接続/失効/失敗でサンプルにフォールバックした場合 true。
  // この時のみ「接続してください」誘導を表示する（apiClient 未注入の純モック表示では出さない）。
  readonly sampleMode?: boolean;
}

const STATUS_LABEL: Record<FindingStatus, string> = { pass: "合格", fail: "要対応", na: "対象外", review: "要確認" };

export function renderFindings(root: HTMLElement, deps: FindingsDeps): void {
  root.replaceChildren();

  const activeFindings = deps.findings ?? LATEST_FINDINGS;
  const activeNotEvaluated = deps.notEvaluated ?? [];
  const summary =
    deps.findings !== undefined ? buildFindingsSummary(deps.findings, computeScore(deps.findings)) : undefined;

  // サンプルフォールバック時の「接続してください」誘導（mock honesty）。
  if (deps.sampleMode === true) {
    const sampleNote = document.createElement("p");
    sampleNote.dataset["testid"] = "sample-notice";
    sampleNote.className = "mb-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800";
    sampleNote.textContent = "サンプルデータを表示中です。実データは「接続」タブで Salesforce と接続してください。";
    root.append(sampleNote);
  }

  const drawer = createDrawer({ title: "検出詳細" });
  function openDetail(f: Finding): void {
    drawer.setTitle(f.check.title);
    drawer.setContent(createFindingDetail(f, { openAssistant: (context) => deps.openAssistant(context, summary) }));
    drawer.open();
  }

  const columns: FtColumn<Finding>[] = [
    { key: "severity", header: "重大度", cell: (f) => createSeverityBadge(f.check.severity), sortable: true, sortAccessor: (f) => SEVERITY_ORDER[f.check.severity] },
    { key: "category", header: "カテゴリ", cell: (f) => CATEGORIES.find((c) => c.key === f.check.category)?.label ?? f.check.category, sortable: true, sortAccessor: (f) => f.check.category },
    { key: "title", header: "チェック", cell: (f) => f.check.title, sortable: true, sortAccessor: (f) => f.check.title },
    { key: "status", header: "状態", cell: (f) => STATUS_LABEL[f.status] },
    { key: "affected", header: "影響数", cell: (f) => String(f.affectedCount), sortable: true, sortAccessor: (f) => f.affectedCount },
  ];

  const filters: FilterDef<Finding>[] = [
    { id: "category", label: "カテゴリ", options: CATEGORIES.map((c) => ({ value: c.key, label: c.label })), accessor: (f) => f.check.category },
    { id: "severity", label: "重大度", options: (["critical", "high", "medium", "low"] as const).map((s) => ({ value: s, label: SEVERITY_LABELS[s] })), accessor: (f) => f.check.severity },
    { id: "status", label: "状態", options: [{ value: "fail", label: "要対応" }, { value: "review", label: "要確認" }, { value: "pass", label: "合格" }, { value: "na", label: "対象外" }], accessor: (f) => f.status, defaultValue: "fail" },
  ];

  const bar = document.createElement("div");
  bar.className = "mb-2 flex justify-end";
  const csvBtn = document.createElement("button");
  csvBtn.type = "button";
  csvBtn.className = "rounded border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100";
  csvBtn.textContent = "CSV 出力";
  csvBtn.addEventListener("click", () => downloadCsv("salesforce-findings.csv", findingsToCsv(activeFindings)));
  bar.append(csvBtn);

  const table = createFilterableTable<Finding>({
    caption: "Salesforce 監査の検出一覧",
    columns,
    rows: activeFindings,
    filters,
    searchable: true,
    searchAccessor: (f) => f.check.title,
    onRowClick: openDetail,
    emptyMessage: "該当する検出はありません。",
    rowTestId: "finding-row",
  });

  // 未評価チェックの正直な表示（合否カウントには含まれない）。
  if (activeNotEvaluated.length > 0) {
    const notice = document.createElement("p");
    notice.className = "mt-3 text-sm text-slate-500";
    notice.dataset["testid"] = "not-evaluated-notice";
    notice.textContent = "未評価 " + activeNotEvaluated.length + " 件（本診断の対象外・後続対応）";
    root.append(bar, table, notice, drawer.element);
  } else {
    root.append(bar, table, drawer.element);
  }
}
